import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import { PiKeyboard, PiSmiley } from 'react-icons/pi';
import type { SkinTonesValue } from 'emoji-picker-react/primitives';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { InlineKeyboardPanel } from 'src/modules/shared/components/molecules/inline-keyboard-panel';
import { EmojiPicker } from 'src/modules/shared/components/molecules/emoji-picker';
import { Popover } from 'src/modules/shared/components/molecules/popovers/popover';
import { CloseRequestContext } from 'src/modules/shared/contexts/close-request-context';
import { useBackgroundFocusRetention } from 'src/modules/shared/hooks/use-background-focus-retention';
import { isInteractiveTarget } from 'src/modules/shared/utils/is-interactive-target.util';
import { useCloseRequest } from 'src/modules/shared/hooks/use-close-request';
import { useMediaQuery } from 'src/modules/shared/hooks/use-media-query';
import { useAnchoredOverlayLayout } from 'src/modules/shared/hooks/use-anchored-overlay-layout';
import { useKeyboardPanel } from 'src/modules/shared/hooks/use-keyboard-panel';
import { useInlinePanelHeight } from 'src/modules/shared/hooks/use-inline-panel-height';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { ComposerEmojiPickerProps } from '../../../types/composer-emoji-picker.types';

/**
 * Connects a shared editor to desktop popover and touch dock presentations.
 * @param props - Generic input actions, controlled visibility and layout slot.
 * @returns The round trigger and the owning surface's emoji panel.
 */
export const ComposerEmojiPicker = ({
  inputRef,
  mobileContainerRef,
  heightBoundary,
  disabled,
  open,
  onOpenChange,
}: ComposerEmojiPickerProps) => {
  const { t } = useI18n();
  const touchLayout = useMediaQuery('(max-width: 767px), (pointer: coarse)');
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const setTriggerRef = useCallback((element: HTMLElement | null) => {
    triggerRef.current = element instanceof HTMLButtonElement ? element : null;
  }, []);
  const panelRef = useRef<HTMLDivElement>(null);
  useBackgroundFocusRetention(
    panelRef,
    heightBoundary?.containerRef,
    open && touchLayout && !disabled
  );
  const gestureStartedInside = useRef(false);
  const panelId = useId();
  const [searchValue, setSearchValue] = useState('');
  const dismissSearchKeyboard = useCallback(() => {
    triggerRef.current?.focus({ preventScroll: true });
  }, []);
  const [skinTone, setSkinTone] = useState<SkinTonesValue>('neutral');
  const layout = useAnchoredOverlayLayout({
    anchorRef: triggerRef,
    isOpen: open && !touchLayout,
    gap: 8,
  });
  const {
    searching,
    preferredHeight,
    occludedHeight,
    retained,
    present,
    prepareOpen,
    prepareReturn,
    exitSearch,
  } = useKeyboardPanel({
    enabled: touchLayout && !disabled,
    open,
    panelRef,
    fallbackHeight: 300,
    fallbackViewportRatio: 0.4,
    onSearchDismiss: dismissSearchKeyboard,
  });
  const searchBoundary = useMemo(
    () =>
      searching && heightBoundary
        ? { ...heightBoundary, minimumFlexibleHeight: 0 }
        : heightBoundary,
    [searching, heightBoundary]
  );
  const dockHeight = useInlinePanelHeight({
    enabled: present,
    panelRef,
    heightBoundary: searchBoundary,
    preferredHeight: searching
      ? Math.min(preferredHeight, 180)
      : preferredHeight,
    occludedHeight,
  });

  const close = useCallback(() => {
    onOpenChange(false);
    triggerRef.current?.focus({ preventScroll: true });
  }, [onOpenChange]);
  const returnToInput = () => {
    // Focus can synchronously start native viewport movement. Commit its
    // replacement geometry first, while preserving the user's focus gesture.
    flushSync(() => {
      prepareReturn();
      onOpenChange(false);
    });
    inputRef.current?.focus();
  };
  const changeOpen = (next: boolean) => {
    if (!next) {
      onOpenChange(false);
      return;
    }
    if (disabled || !inputRef.current?.captureSelection()) return;
    if (touchLayout) {
      flushSync(() => {
        prepareOpen();
        onOpenChange(true);
      });
    } else {
      onOpenChange(true);
    }
    // Retain native editor focus where supported to avoid a keyboard layout
    // change during dismissal. Other browsers keep the normal focus fallback.
    if (touchLayout && !inputRef.current.hideKeyboard()) {
      triggerRef.current?.focus({ preventScroll: true });
    }
  };
  const layerId = useCloseRequest({
    // Preserve the close listener during search, ahead of catalog Escape handlers.
    isOpen: open && touchLayout,
    onClose: searching ? exitSearch : close,
  });

  useEffect(() => {
    if (!open) setSearchValue('');
  }, [open]);

  useEffect(() => {
    if (disabled && open) onOpenChange(false);
  }, [disabled, open, onOpenChange]);

  useEffect(() => {
    if (!open || !touchLayout) {
      gestureStartedInside.current = false;
      return;
    }
    const outside = (event: Event) => {
      if (!(event.target instanceof Node)) return;
      const inside =
        (panelRef.current?.contains(event.target) ?? false) ||
        (triggerRef.current?.contains(event.target) ?? false);
      if (event.type === 'pointerdown') gestureStartedInside.current = inside;
      if (event.type === 'click') {
        // Search focus can move the dock before touch produces its mouse click.
        // Keep ownership of that gesture, even when Chrome retargets the click
        // to a shared ancestor. Keyboard activation still dismisses normally.
        const fromInside =
          gestureStartedInside.current &&
          event instanceof MouseEvent &&
          event.detail > 0;
        gestureStartedInside.current = false;
        if (fromInside) return;
      }
      if (!inside) {
        if (event.target instanceof HTMLTextAreaElement) {
          if (event.type === 'pointerdown') {
            flushSync(() => {
              prepareReturn();
              onOpenChange(false);
            });
          }
        } else if (
          event.type === 'click' &&
          isInteractiveTarget(event.target)
        ) {
          // Let an outside action receive a completed gesture before changing
          // the layout underneath it (e.g. the attachment menu trigger).
          onOpenChange(false);
        }
      }
    };
    const cancelGesture = () => {
      gestureStartedInside.current = false;
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('pointercancel', cancelGesture, true);
    document.addEventListener('click', outside, true);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('pointercancel', cancelGesture, true);
      document.removeEventListener('click', outside, true);
    };
  }, [open, touchLayout, onOpenChange, prepareReturn]);

  const toggleTouch = () => {
    if (open) returnToInput();
    else changeOpen(true);
  };
  const trigger = (
    <Button
      ref={triggerRef}
      type="button"
      size="md"
      appearance="ghost"
      variant="primary"
      shape="round"
      isIconOnly
      disabled={disabled}
      aria-label={t(
        touchLayout && open ? 'emojiPicker.keyboard' : 'emojiPicker.open'
      )}
      aria-expanded={open}
      aria-controls={open ? panelId : undefined}
      aria-haspopup="dialog"
      className={open ? 'bg-neutral-100' : ''}
      onPointerDown={(event) => {
        if (!inputRef.current?.captureSelection() || touchLayout) {
          event.preventDefault();
        }
      }}
      onClick={touchLayout ? toggleTouch : undefined}
    >
      {touchLayout && open ? (
        <PiKeyboard className="h-5 w-5" aria-hidden />
      ) : (
        <PiSmiley className="h-5 w-5" aria-hidden />
      )}
    </Button>
  );
  const content = (
    <EmojiPicker
      onSelect={(emoji) => {
        inputRef.current?.insertText(emoji);
      }}
      skinTone={skinTone}
      onSkinToneChange={setSkinTone}
      autoFocusSearch={!touchLayout}
      compact={touchLayout}
      searchMode={searching || (touchLayout && searchValue.length > 0)}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      onClose={close}
    />
  );

  if (touchLayout) {
    return (
      <CloseRequestContext.Provider value={layerId}>
        {trigger}
        <InlineKeyboardPanel
          open={open}
          retained={retained}
          containerRef={mobileContainerRef}
          panelRef={panelRef}
          id={panelId}
          label={t('emojiPicker.title')}
          height={dockHeight}
          occludedHeight={occludedHeight}
        >
          <div data-emoji-board className="h-full px-3">
            {content}
          </div>
        </InlineKeyboardPanel>
      </CloseRequestContext.Provider>
    );
  }
  return (
    <Popover
      handler={trigger}
      nativeHandlerRef={setTriggerRef}
      open={open}
      disabled={disabled}
      onOpenChange={changeOpen}
      placement={`${layout.side}-start`}
      contentId={panelId}
      contentAriaLabel={t('emojiPicker.title')}
      contentClassName="w-[min(352px,calc(100vw-24px))] overflow-hidden rounded-xl border-neutral-200"
    >
      {open && (
        <div
          ref={panelRef}
          style={{ height: Math.min(365, layout.maxHeight ?? 365) }}
        >
          {content}
        </div>
      )}
    </Popover>
  );
};
